import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { ControlContainer, FormGroupDirective, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { ControlPropertyModel } from '@common/models/control-property.model';
import { marker } from '@common/translate-marker';
import { isMobile } from '@common/utils/isMobile';

@Component({
    selector: 'auto-form',
    styleUrls: ['form-controls.component.scss'],
    template: `
        <div class="fields-container card-body">
            @for (prop of properties; track prop) {
                <div [ngClass]="fieldClass()">
                    @if (prop.dataType === 'date') {
                        <div class="input">
                            <form-date [labelText]="formatName(prop.label)" [formControlName]="prop.key"></form-date>
                        </div>
                    }
                    @if (datetimeProps.includes(prop.dataType)) {
                        <div class="input">
                            <form-datetime
                                [labelText]="formatName(prop.label)"
                                [formControlName]="prop.key"></form-datetime>
                        </div>
                    }
                    @if (prop.dataType === 'time') {
                        <div class="input">
                            <form-time [labelText]="formatName(prop.label)" [formControlName]="prop.key"></form-time>
                        </div>
                    }
                    @if (prop.dataType === 'text') {
                        <div class="input">
                            @if (prop.key !== 'Name' && prop.key !== 'Code') {
                                <form-textbox
                                    [labelText]="formatName(prop.label)"
                                    [formControlName]="prop.key"></form-textbox>
                            }
                            @if (
                                (prop.key === 'Name' || prop.key === 'Code') &&
                                entity.includes('CodeList') &&
                                entity !== 'CodeListPortPerSystem'
                            ) {
                                <form-textbox
                                    [labelText]="formatName(prop.label)"
                                    [formControlName]="prop.key"></form-textbox>
                            }
                        </div>
                    }
                    @if (prop.dataType === 'number') {
                        <div class="input">
                            <form-number
                                [labelText]="formatName(prop.label)"
                                [formControlName]="prop.key"></form-number>
                        </div>
                    }
                    @if (prop.dataType === 'boolean') {
                        <div class="input">
                            <form-checkbox
                                [labelText]="formatName(prop.label)"
                                [tooltipTitle]="getTooltipTitle(prop)"
                                [formControlName]="prop.key"></form-checkbox>
                        </div>
                    }
                    @if (!editMode) {
                        @if (prop.dataType === 'codeList') {
                            <div class="input">
                                <form-dropdown
                                    [labelText]="formatName(prop.label)"
                                    [formControlName]="prop.key"
                                    [codeListName]="prop.codeListName"></form-dropdown>
                            </div>
                        }
                    }
                    @if (editMode) {
                        @if (prop.dataType === 'codeList') {
                            <div class="input">
                                <form-dropdown
                                    [labelText]="formatName(prop.label)"
                                    [formControlName]="prop.key === 'System' ? 'systemId' : prop.key"
                                    [codeListName]="prop.codeListName"></form-dropdown>
                            </div>
                        }
                    }
                </div>
            }
            <ng-content></ng-content>
        </div>
    `,
    viewProviders: [
        {
            provide: ControlContainer,
            useExisting: FormGroupDirective
        }
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AutoFormComponent {
    @Input() formGroup: UntypedFormGroup;
    @Input() properties: ControlPropertyModel[];
    @Input() entity: string;
    @Input() systemId: UntypedFormControl;
    @Input() editMode: boolean;

    datetimeProps = ['date', 'time', 'dateTime', 'datetime', 'DateTime', 'Date'] as string[];

    private tooltipTitles: { [key: string]: string } = {
        'AdditionalServicePerSystem.AllowCombiningAddsServ': marker(
            'Determines whether users are permitted to bundle multiple additional services together when creating a Service Request. When this setting is enabled (checked), users can add other additional services that also have this flag active alongside the selected service.'
        ),
        'ManipulationPerSystem.AllowUpdateAfterSend': marker(
            'Controls whether Means of Transport (MOT) details can be updated after a Service Request has already been submitted. If enabled, and if the user has the required permissions, MOT identifiers (e.g. truck license plates or wagon numbers) may be modified after submission, subject to additional validation rules.'
        ),
        'ManipulationPerSystem.AllowDirectManipulationDocument': marker(
            'If checked, allows direct manipulations to be performed on standard service requests (e.g. an unload vessel request allows a direct vessel-to-truck manipulation on a direct storage document).'
        ),
        'CustomsStatusPerSystem.RequiresCustomsApproval': marker(
            'If checked, this customs status will trigger an approval workflow within the selected terminal system.'
        ),
        'CustomsWarehouseType.RequiresCustomsApproval': marker(
            'If checked, this warehouse type will require automated customs oversight and approval workflows.'
        )
    };

    constructor() {}

    fieldClass() {
        if (isMobile()) {
            return 'mobile-field';
        }
        return 'regular-field';
    }

    formatName(name: string): string {
        return name
            ? name
                  .replace(/([A-Z])/g, ' $1')
                  .replace(/\s+/g, ' ')
                  .trim()
            : '';
    }

    getTooltipTitle(prop: ControlPropertyModel): string {
        return this.tooltipTitles[`${this.entity}.${prop.key}`];
    }
}
